import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Pager, ListStates } from './SyncStatusBrowser';
import { ViewHeader } from './DashboardLayout';
import type {
  AuditLogEntry,
  AuditActionFilter,
  ListResult,
} from './types';

/**
 * Dashboard → Audit log (M10c).
 *
 * Paginated, filterable view of the tenant's `audit_log` (every state-changing
 * action — onboarding, provisioning, sync_state transitions, admin Link/
 * Create/Ignore/Relink, webhook failures). The audit_log lives in the
 * per-tenant DO; this view hits `GET /api/admin/audit/list`.
 *
 * Filter UX: a small set of quick-filter tabs (the actions admins care about
 * most) + an "All" tab + a free-text search over resource_id / user_email.
 * Pagination via the shared `Pager` primitive (same as the M10b list views).
 *
 * Reference: src/lib/audit.ts (AUDIT_ACTIONS vocabulary + readAuditLogPage)
 *            db/tenant.sql (audit_log DDL + CHECK constraint)
 *            docs/features/sync-control.md (FR-SYNCCTL-6: every transition audited)
 */

/** The quick-filter tabs. `all` = no action filter; the rest are the actions
 *  admins most often triage, grouped by category. The full vocabulary is
 *  reachable via 'All' + free-text search on resource_id. */
const ACTION_TABS: { value: AuditActionFilter; label: string; }[] = [
  { value: 'all', label: 'All actions' },
  { value: 'subscription_cancel', label: 'Cancels' },
  { value: 'refund_issue', label: 'Refunds' },
  { value: 'deal_create', label: 'Deal creates' },
  { value: 'deal_update', label: 'Deal updates' },
  { value: 'contact_update', label: 'Contact updates' },
  { value: 'role_change', label: 'Role changes' },
  { value: 'login', label: 'Logins' },
  { value: 'settings_update', label: 'Settings' },
];

const PAGE_SIZE = 25;

export function Audit() {
  const [action, setAction] = useState<AuditActionFilter>('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [offset, setOffset] = useState(0);

  // Debounce the search box (350ms) so typing doesn't spam the API.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setOffset(0); // a new search resets to page 1
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const query = useMemo(() => {
    const qs = new URLSearchParams();
    if (action !== 'all') qs.set('action', action);
    qs.set('limit', String(PAGE_SIZE));
    qs.set('offset', String(offset));
    if (debouncedSearch) qs.set('search', debouncedSearch);
    return qs.toString();
  }, [action, offset, debouncedSearch]);

  const fetcher = useCallback(
    (signal: AbortSignal) =>
      api.get<ListResult<AuditLogEntry>>(`admin/audit/list?${query}`, {
        signal,
      }),
    [query]
  );
  const { data, loading, error, refetch } = useAsync(fetcher, [query]);

  return (
    <>
      <ViewHeader
        title="Audit log"
        subtitle="Every mutation to Stripe or HubSpot — human actions, automated sync, role changes, and logins."
      />

      {/* Filter tabs + search */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-1">
          {ACTION_TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => {
                setAction(tab.value);
                setOffset(0);
              }}
              className={[
                'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                action === tab.value
                  ? 'bg-brand-600 text-white'
                  : 'bg-ink-100 text-ink-600 hover:bg-ink-200',
              ].join(' ')}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search resource id or user…"
          className="ml-auto w-64 rounded-md border border-ink-200 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
        />
      </div>

      {(loading || error || (data && data.items.length === 0)) && (
        <ListStates
          loading={loading}
          error={error}
          empty={!!data && data.items.length === 0}
          onRetry={refetch}
        />
      )}

      {data && data.items.length > 0 && (
        <>
          <div className="overflow-hidden rounded-xl border border-ink-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-ink-50 text-left text-xs uppercase tracking-wide text-ink-500">
                <tr>
                  <th className="px-4 py-2 font-medium">When</th>
                  <th className="px-4 py-2 font-medium">Action</th>
                  <th className="px-4 py-2 font-medium">Resource</th>
                  <th className="px-4 py-2 font-medium">User</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {data.items.map((entry) => (
                  <AuditRow key={entry.id} entry={entry} />
                ))}
              </tbody>
            </table>
          </div>
          <Pager
            total={data.total}
            offset={data.offset}
            pageSize={data.limit}
            setOffset={setOffset}
          />
        </>
      )}
    </>
  );
}

function AuditRow({ entry }: { entry: AuditLogEntry; }) {
  const ts = formatTimestamp(entry.createdAt);
  const isSystem = entry.userEmail === 'system' || entry.userEmail === null;
  return (
    <tr className="align-top hover:bg-ink-50/50">
      <td
        className="whitespace-nowrap px-4 py-2 text-ink-500"
        title={ts.absolute}
      >
        {ts.relative}
      </td>
      <td className="px-4 py-2">
        <code className="rounded bg-ink-100 px-1.5 py-0.5 text-xs text-ink-700">
          {entry.action}
        </code>
      </td>
      <td className="max-w-xs truncate px-4 py-2 font-mono text-xs text-ink-600">
        {entry.resourceId ?? <span className="text-ink-300">—</span>}
      </td>
      <td className="whitespace-nowrap px-4 py-2 text-ink-600">
        {isSystem
          ? <span className="text-ink-400 italic">System (automated)</span>
          : entry.userEmail}
      </td>
    </tr>
  );
}

/** Format a Unix-seconds timestamp as a short relative + absolute pair. */
function formatTimestamp(unixSeconds: number): {
  relative: string;
  absolute: string;
} {
  const date = new Date(unixSeconds * 1000);
  const absolute = date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  const diffSec = Math.floor(Date.now() / 1000) - unixSeconds;
  let relative: string;
  if (diffSec < 60) relative = `${diffSec}s ago`;
  else if (diffSec < 3600) relative = `${Math.floor(diffSec / 60)}m ago`;
  else if (diffSec < 86400) relative = `${Math.floor(diffSec / 3600)}h ago`;
  else relative = `${Math.floor(diffSec / 86400)}d ago`;
  return { relative, absolute };
}
